import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Modal, Form, Input, Radio, Table, Typography, Tag, Row,
} from 'antd';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import {
  selectEditState, selectSubmitChecklistItemRequest, selectChecklistItemReferencesRequest,
} from '../../../../state/ui/configuration/inspection/checklistItemUiReducer';
import { selectChecklistItem } from '../../../../state/domain/checklistItems/checklistItemsReducer';
import {
  cancelEditChecklistItem, requestAddChecklistItem, requestUpdateChecklistItem, requestForChecklistItemReferences,
} from '../../../../state/ui/configuration/inspection/checklistItemUiActions';
import { codeAvailabilityActions } from './config-util';
import { selectChecklistItemReferences } from '../../../../state/domain/checklistItems/checklistItemReferencesReducer';


const { Column } = Table;
const { Text } = Typography;

/**
 * The field for the description label. Allows clear, and max length set. Required plus extra duplicate validation
 * The rules are injected into the form using the form field decorator.
 */
const ItemDescriptionField = injectIntl(({
  form, checklistItem, intl, checkForDuplicates,
}) => (
  <Form.Item required label={<FormattedMessage id="question" />}>
    { form.getFieldDecorator('itemDescription', {
      initialValue: checklistItem ? checklistItem.itemDescription : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        {
          validator(rule, value, callback) {
            if (value !== (checklistItem ? checklistItem.itemDescription : undefined)
              && checkForDuplicates(checklistItem ? checklistItem.itemCode : null, value)) {
              callback(intl.formatMessage({ id: 'Duplicate' }));
            }
            callback();
          },
        },
      ],
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the publish label.  Toggle that also controls mandatory flag on checklist.
 * The rules are injected into the form using the form field decorator.
 */
const PublishField = injectIntl(({ form, checklistItem, intl }) => (
  <Form.Item label={<FormattedMessage id="published" />}>
    { form.getFieldDecorator('active', {
      initialValue: checklistItem ? checklistItem.active : false,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <Radio.Group buttonStyle="solid">
        <Radio.Button value={codeAvailabilityActions.PUBLISH}><FormattedMessage id="published" /></Radio.Button>
        <Radio.Button value={codeAvailabilityActions.UNPUBLISH}><FormattedMessage id="notPublished" /></Radio.Button>
      </Radio.Group>,
    )}
  </Form.Item>
));

function ChecklistItemReferencesTable({
  checklistItemReferences,
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${checklistItemReferences.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  return (
    <Form.Item label={
      (
        <div>
          <FormattedMessage id="references1" />
          <FormattedMessage id="checklists" />
        </div>
    )}
    >
      <Table
        rowKey={checklistItemRefs => checklistItemRefs.checkListCode}
        dataSource={checklistItemReferences}
        size="small"
        showHeader={false}
        bordered
        pagination={{ defaultPageSize: 8, showTotal: () => <RowTotal /> }}
      >
        <Column
          dataIndex="description"
          key="description"
        />
      </Table>
    </Form.Item>
  );
}

/**
 * Validation for the props on the component.
 */
ChecklistItemReferencesTable.propTypes = {
  checklistItemReferences: PropTypes.arrayOf(
    PropTypes.shape({
      checklistItemDescription: PropTypes.string,
    }),
  ),
};

/**
 * Default data for the component if nothing has been supplied.
 * Only needed for non-required props.
 */
ChecklistItemReferencesTable.defaultProps = {
  checklistItemReferences: null,
};

const DisplayChecklistQuestionFields = injectIntl((Form.create({ name: 'new-question' })(({
  form, checklistItem, intl,
}) => {
  const [digitsDisabled, setDigitsDisabled] = useState(true);

  return (
    <Row>
      <Form.Item required label={<FormattedMessage id="answerChoice" />}>
        {form.getFieldDecorator('itemDataType', {
          rules: [
            {
              required: true,
              message: intl.formatMessage({ id: 'validationEmpty' }),
            },
          ],
          initialValue: checklistItem ? checklistItem.itemDataType : 'BOOLEAN',
        })(
          <Radio.Group
            buttonStyle="solid"
            onChange={(e) => {
              setDigitsDisabled(!(e.target.value === 'INTEGER' || e.target.value === 'INTEGER_NA'));
            }}
          >
            <Radio.Button value="BOOLEAN">
              <FormattedMessage id="pass" />
            &nbsp;|&nbsp;
              <FormattedMessage id="fail" />
            </Radio.Button>
            <Radio.Button value="BOOLEAN_NA">
              <FormattedMessage id="NA" />
            &nbsp;|&nbsp;
              <FormattedMessage id="pass" />
            &nbsp;|&nbsp;
              <FormattedMessage id="fail" />
            </Radio.Button>
            <Radio.Button value="INTEGER">
              <FormattedMessage id="number" />
            </Radio.Button>
            <Radio.Button value="INTEGER_NA">
              <FormattedMessage id="NA" />
            &nbsp;|&nbsp;
              <FormattedMessage id="number" />
            </Radio.Button>
          </Radio.Group>,
        )}
      </Form.Item>
      <Form.Item required label={<FormattedMessage id="numberRange" />}>
        {form.getFieldDecorator('itemDigitCount', {
          rules: [
            {
              required: form.getFieldValue('itemDataType') === 'INTEGER' || form.getFieldValue('itemDataType') === 'INTEGER_NA',
              message: intl.formatMessage({ id: 'validationEmpty' }),
            },
          ],
          initialValue: checklistItem ? checklistItem.itemDigitCount : 1,
        })(
          <Radio.Group
            buttonStyle="solid"
            disabled={digitsDisabled}
          >
            <Radio.Button value={1}>
              <FormattedMessage id="1digit" />
            </Radio.Button>
            <Radio.Button value={2}>
              <FormattedMessage id="2digits" />
            </Radio.Button>
            <Radio.Button value={3}>
              <FormattedMessage id="3digits" />
            </Radio.Button>
          </Radio.Group>,
        )}
      </Form.Item>
      <Form.Item label={<FormattedMessage id="preview" />}>
        <Row type="flex" justify="start">
          { form.getFieldValue('itemDataType').toString().endsWith('_NA')
            ? <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#3399ff"><FormattedMessage id="NA" /></Tag>
            : '' }
          { form.getFieldValue('itemDataType').toString().startsWith('BOOLEAN')
            ? (
              <div>
                <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#00b33c"><FormattedMessage id="pass" /></Tag>
                <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#e68a00"><FormattedMessage id="fail" /></Tag>
              </div>
            )
            : '' }
          { form.getFieldValue('itemDataType').toString().startsWith('INTEGER') && form.getFieldValue('itemDigitCount') === 1
            ? <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#5c5c8a">0-9</Tag>
            : '' }
          { form.getFieldValue('itemDataType').toString().startsWith('INTEGER') && form.getFieldValue('itemDigitCount') === 2
            ? <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#5c5c8a">0-99</Tag>
            : '' }
          { form.getFieldValue('itemDataType').toString().startsWith('INTEGER') && form.getFieldValue('itemDigitCount') === 3
            ? <Tag style={{ padding: '0.5em 1em' }} className="via-checklist-tag" color="#5c5c8a">0-999</Tag>
            : '' }
        </Row>
      </Form.Item>
    </Row>
  );
})));

const EditChecklistItem = injectIntl(Form.create({ name: 'edit-checklist-item' })(({
  intl, form, visible, checklistItem, onCreate, onUpdate, onCancel, checklistItemsLoading, checklistItems,
  isSubmitting, validationBox, descValidator, checklistItemReferences, loadChecklistItemReferences, ...rest
}) => {
  const isCreating = !checklistItem;
  const titleId = isCreating ? 'addNewQuestion' : 'editQuestion';
  const buttonId = 'save';

  const { resetFields } = form;

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      if (!isCreating) loadChecklistItemReferences(checklistItem);
      resetFields();
    }
  }, [resetFields, visible, checklistItem, loadChecklistItemReferences, isCreating]);

  // On form submit.
  const submitChecklistItem = useCallback(() => {
    form.validateFields((err, values) => {
      if (err) return; // Nothing more to do.

      if (isCreating) {
        onCreate({ ...values });
      } else {
        const updatedChecklistItem = {
          ...checklistItem,
          ...values,
        };
        onUpdate(updatedChecklistItem);
      }
    });
  }, [form, checklistItem, isCreating, onCreate, onUpdate]);

  return (
    <Modal
      title={<FormattedMessage id={titleId} />}
      okText={<FormattedMessage id={buttonId} />}
      visible={visible}
      onOk={submitChecklistItem}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      {...rest}
    >
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitChecklistItem(); }}>
        <ViaValidationAlert validationError={validationBox} />
        <ItemDescriptionField form={form} checklistItem={checklistItem} checkForDuplicates={descValidator} />
        <DisplayChecklistQuestionFields form={form} checklistItem={checklistItem} />
        <PublishField form={form} checklistItem={checklistItem} />
        {
          !isCreating ? <ChecklistItemReferencesTable checklistItemReferences={checklistItemReferences} /> : undefined
        }
      </Form>
    </Modal>
  );
}));

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectSubmitChecklistItemRequest(state);
  const checklistItemReferencesRequest = selectChecklistItemReferencesRequest(state);
  const checklistItemReferences = selectChecklistItemReferences(state);

  return {
    visible: editState.isEditing,
    checklistItem: editState.itemCode ? selectChecklistItem(state, editState.itemCode) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
    checklistItemReferencesIsLoading: checklistItemReferencesRequest ? checklistItemReferencesRequest.isLoading : false,
    checklistItemReferences: checklistItemReferences || [],
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: (checklistItem, newChecklistItems) => dispatch(requestAddChecklistItem(checklistItem, newChecklistItems)),
  onUpdate: (checklistItem, newChecklistItems) => dispatch(requestUpdateChecklistItem(checklistItem, newChecklistItems)),
  onCancel: () => dispatch(cancelEditChecklistItem()),
  loadChecklistItemReferences:
  checklistItem => dispatch(requestForChecklistItemReferences(checklistItem ? checklistItem.itemCode : null)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditChecklistItem);
